import FreshPaint from "@/app/_shared/FreshPaint";
import {
  ElevatedButton,
  ElevatedElement,
} from "@/app/_shared/VisuallyNestedLink";
import { MatchInfo2 } from "@/app/consults-and-matching/_components/MyMatchesTableItem";
import MatchQueueItem from "@/app/consults-and-matching/_components/MyMatchesTableItem/MatchQueueItem2";
import { Container } from "@/app/dashboard/dashboard";
import {
  Button,
  CSS_COLORS,
  Flex,
  RadixTooltip,
  styledStitches,
} from "@/app/design-system";

import {
  FOCUS_STYLES,
  TABLE_LINK_STYLE,
} from "@/app/design-system/styles/config/css-utils";

import { CommonAbbr } from "@/app/_shared/CommonAbbr";
import ExternalLink from "@/app/_shared/ExternalLink";
import Mangle from "@/app/_shared/Mangle";
import {
  ScreenReaderOnlyText,
  TooltipContentInTableScreenReaderOnlyText,
} from "@/app/_shared/ScreenReaderOnlyText";
import { UnstyledButton } from "@/app/design-system/button/button";
import { PersonIcon, WashingMachineCycleIcon } from "@/app/design-system/icons";
import { AlertTriangle } from "@/app/design-system/icons/components/alert-triangle";
import { CommonAbbrTag, Tag } from "@/app/design-system/tag/tag";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Spin } from "antd";
import { DateTime } from "luxon";
import React, { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { NoDataMessage } from "../../../dashboard/_components/no-data-message";
import { UnsortableDashboardTable } from "../../../dashboard2.0/_components/dashboard-table";
import { HintTextDiv } from "../../../panel-management/_shared";
import { PaginationInput } from "../_components";

import {
  CloseButton,
  Content,
  ModalIconButton,
  Overlay,
  Root,
} from "@/app/design-system/modal-dialog/modal-dialog.styled";

import * as Dialog from "@radix-ui/react-dialog";

import { XIcon } from "@/app/design-system/icons";
import { FlagIcon } from "@/app/design-system/icons/components/flag-icon";
import { ThreeDotsIcon } from "@/app/design-system/icons/components/three-dots-icon";
import { useQueryClient } from "react-query";
import {
  FormattedPastRow,
  FormattedRecentConsult,
  getClientsMapFromPastConsultsData,
  getConsultNoteLabel,
  getConsultToolLabel,
  isAssessmentAppointmentType,
} from "../_utils";
import {
  INCOMPLETE_MY_CONSULTS_VALUE,
  INIT_FILTERS,
  IN_QUEUE_STATUS,
  PAST_MY_CONSULTS_VALUE,
  SEARCH_ALL,
} from "../consts";
import { matchesByClinicianIdQueryKey } from "../use-get-matches-by-client-id";
import { myConsultsQueryKey } from "../use-get-my-consults";
import { MyPastConsultsSearchResultsQueryKey } from "../use-get-my-past-consults-search";
import { PastConsultsTableContainer } from "./past-consults-table-container";
import { ConsultClinicianClientsContext } from "../consultClinicianClients.context";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/design-system/popover/popover.styled";
import { CurrentUserContext } from "@/app/app.utils";
import { getRoofDogBaseUrl } from "@/app/_helpers";
import { ClockIcon } from "@/app/design-system/icons/components/clock-icon";
import { colors } from "@/app/design-system/styles/config/colors";
import { UserHasAnyPermissions } from "@/app/_helpers/permissions";
import { useGetClientMissingConsents } from "@/app/consults-and-matching/MatchingV2/use-get-client-missing-consents.api";
import { useGetClientDocumentByType } from "@/app/consults-and-matching/MatchingV2/use-get-client-document.api";
import { useIsPathwaysMilestone3 } from "@/app/hooks/use-is-pathways-milestone3";

/**
 * @name TableLink
 * @description Annoying but antd overrides link styles and makes them black, so we have to override them back to our default link styles
 */
export const TableLink = styledStitches(Link, {
  ...TABLE_LINK_STYLE,
  ...FOCUS_STYLES,
  display: "block",
  "&:hover ": {
    color: "#2061DC",
    textDecoration: "none",
  },
  "&:visited": {
    color: "purple",
  },
});

export const ModalIconCloseButton = styledStitches(
  CloseButton,
  ModalIconButton,
  {
    ...FOCUS_STYLES,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "$neutral2",
    },
  },
);
interface GetColumnsProps {
  activeTab: string;
  matchData: FormattedRecentConsult[] | FormattedPastRow[];
  refetchMatchesByClinicianId: any;
  refetchMyConsults: any;
  isPathwaysMilestone3: boolean;
}

export const getColumns = ({
  activeTab,
  matchData,
  refetchMatchesByClinicianId,
  refetchMyConsults,
  isPathwaysMilestone3,
}: GetColumnsProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const hasConsultPermissions = true;
  const noteLinkSrLabel = isPathwaysMilestone3
    ? "Assessment Note Link"
    : "Consult Note Link";
  const noteModalSrLabel = isPathwaysMilestone3
    ? "Assessment Note Modal"
    : "Consult Note Modal";
  const toolSrLabel = isPathwaysMilestone3
    ? "Assessment Tool"
    : "Consult Tool";

  const clientId =
    activeTab === PAST_MY_CONSULTS_VALUE || activeTab === SEARCH_ALL
      ? // @ts-ignore
        matchData[0]?.client.clientId
      : // @ts-ignore
        matchData[0]?.client?.id;

  const columns = [
    {
      accessorKey: "client",
      id: "fullName",
      header: () => <span>Client</span>,
      cell: (info: any) => {
        const original: FormattedRecentConsult = info.row.original;
        const client = info.getValue();
        const clientId =
          activeTab === PAST_MY_CONSULTS_VALUE || activeTab === SEARCH_ALL
            ? client.clientId
            : client?.id;
        const fullName = client?.fullName;
        const payerAcronym = client?.payerAcronym;
        const payerDisplayName = client?.payerDisplayName;
        const isFormerClient = client?.isFormerClient;
        const isHighPriority = client?.isHighPriority;
        const isRematch = client?.isRematch;
        const timeScheduled = DateTime.fromISO(original.consult.timeScheduled);
        const currentUser = React.useContext(CurrentUserContext);

        return (
          <>
            <FreshPaint eventName="Matching V2: Clicked client link - client name">
              <Flex style={{ alignItems: "center" }}>
                <TableLink aria-hidden to={`/my-clients/${clientId}/mbc`}>
                  <Mangle>{fullName}</Mangle>
                </TableLink>
                {UserHasAnyPermissions(currentUser, [
                  "IsDynamicFlexBetaUser",
                ]) &&
                  timeScheduled.hasSame(DateTime.now(), "day") && (
                    <RadixTooltip content="Recently Scheduled">
                      <ElevatedElement style={{ marginLeft: "8px" }}>
                        <ClockIcon
                          title="Recently Scheduled"
                          fill={colors.green11}
                        />
                      </ElevatedElement>
                    </RadixTooltip>
                  )}
                {payerAcronym && (
                  <ElevatedElement>
                    <CommonAbbrTag
                      key={payerAcronym}
                      title={payerDisplayName}
                      style={{ marginLeft: "8px" }}
                    >
                      {payerAcronym}
                    </CommonAbbrTag>
                  </ElevatedElement>
                )}
                {isFormerClient && (
                  <RadixTooltip content={"Former Client"}>
                    <ElevatedButton
                      style={{
                        marginLeft: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <PersonIcon />
                      <TooltipContentInTableScreenReaderOnlyText>
                        Former Client
                      </TooltipContentInTableScreenReaderOnlyText>
                    </ElevatedButton>
                  </RadixTooltip>
                )}
                {isHighPriority && (
                  <RadixTooltip content={"High Risk Client"}>
                    <ElevatedButton
                      style={{
                        marginLeft: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FlagIcon
                        stroke={CSS_COLORS.red11}
                        title="Open warning:"
                      />
                      <TooltipContentInTableScreenReaderOnlyText>
                        High Risk Client
                      </TooltipContentInTableScreenReaderOnlyText>
                    </ElevatedButton>
                  </RadixTooltip>
                )}
                {isRematch && (
                  <RadixTooltip content={"Rematched Client"}>
                    <ElevatedButton
                      style={{
                        marginLeft: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <WashingMachineCycleIcon title="Open warning:" />
                      <TooltipContentInTableScreenReaderOnlyText>
                        Rematched Client
                      </TooltipContentInTableScreenReaderOnlyText>
                    </ElevatedButton>
                  </RadixTooltip>
                )}
              </Flex>
            </FreshPaint>
          </>
        );
      },
    },
    {
      accessorKey: "statusOrStartTime",
      id: "startTime",
      header: () => "Date",
      cell: (info: any) => {
        const statusOrStartTime = info.getValue();
        if (!statusOrStartTime.startTime) {
          return <CommonAbbr title={"Not Applicable"}>N/A</CommonAbbr>;
        }
        let dateFormat = "EEE, LLL d";
        if (activeTab === PAST_MY_CONSULTS_VALUE || activeTab === SEARCH_ALL) {
          dateFormat = "LLL d, yyyy";
        }
        const formattedDate = DateTime.fromISO(
          statusOrStartTime.startTime,
        ).toFormat(dateFormat);
        if (statusOrStartTime.hasConsult) {
          // Prefer the session's own service type so historical consults stay
          // labeled "Consult" even when Pathways milestone 3 is on.
          const serviceType = info.row?.original?.consult?.serviceType;
          const datePrefix = isAssessmentAppointmentType(serviceType)
            ? "Assessment"
            : "Consult";
          return `${datePrefix}: ${formattedDate}`;
        } else {
          return formattedDate;
        }
      },
    },
    {
      accessorKey: "statusOrStartTime",
      id: "time",
      header: () =>
        activeTab === PAST_MY_CONSULTS_VALUE || activeTab === SEARCH_ALL
          ? "Status"
          : "Time",
      cell: (info: any) => {
        const statusOrStartTime = info.getValue();
        if (!statusOrStartTime.status && !statusOrStartTime.startTime) {
          return "N/A";
        }
        if (activeTab === PAST_MY_CONSULTS_VALUE || activeTab === SEARCH_ALL) {
          // Event-backed rows have no match, so there is no client status to report.
          if (!statusOrStartTime.status) {
            return <CommonAbbr title={"Not Applicable"}>N/A</CommonAbbr>;
          }
          let statusString = statusOrStartTime.status;
          let daysSinceStartTime = 0;
          if (statusOrStartTime.status === IN_QUEUE_STATUS) {
            daysSinceStartTime = Math.abs(
              Math.floor(
                DateTime.fromISO(statusOrStartTime.startTime).diffNow("days")
                  .days,
              ),
            );
            if (!isNaN(daysSinceStartTime)) {
              statusString += ` · ${daysSinceStartTime}`;
            }
          }
          let color = "neutral";
          if (
            statusString === "In Queue" ||
            statusString === "Rematched" ||
            statusString === "Referred Out" ||
            statusString === "Requeued"
          ) {
            color = "neutral";
          } else if (statusString === "Did Not Convert") {
            color = "red";
          } else if (statusString === "Prospective Match") {
            color = "yellow";
          } else if (statusString === "Delayed") {
            color = "violet";
          } else if (
            statusString === "Scheduled" ||
            statusString === "Active"
          ) {
            color = "green";
          }
          return (
            <ElevatedElement>
              <Tag
                key={statusString}
                title={statusString}
                style={{ marginLeft: "8px" }}
                // @ts-ignore
                color={color}
              >
                {statusString}
                {statusOrStartTime.status === IN_QUEUE_STATUS &&
                !isNaN(daysSinceStartTime) ? (
                  <CommonAbbr title={daysSinceStartTime === 1 ? "day" : "days"}>
                    d
                  </CommonAbbr>
                ) : null}
              </Tag>
            </ElevatedElement>
          );
        }
        const formattedDate = DateTime.fromISO(
          statusOrStartTime.startTime,
        ).toFormat("ha");
        return formattedDate;
      },
    },
    // Figure out what column to get information from to generate matchmaker link
    {
      accessorKey: "consult",
      header: () => (
        <ScreenReaderOnlyText>{noteLinkSrLabel}</ScreenReaderOnlyText>
      ),
      cell: (info: any) => {
        const consult = info.getValue();
        const healthieNoteId = consult?.healthieNoteId;
        const startTime = consult?.startTime;
        const cutoffDate = new Date("2023-08-28T00:00:00Z");
        const healthieId = consult?.healthieId;

        const serviceType = consult?.serviceType;

        const redirectUrl = `https://twochairs.gethealthie.com/users/${healthieId}/private_notes/${
          healthieNoteId ?? ""
        }`;
        return (
          <>
            <div style={{ whiteSpace: "nowrap" }}>
              <ExternalLink
                style={{ marginRight: "8px", textDecoration: "underline" }}
                href={redirectUrl}
              >
                {getConsultNoteLabel(serviceType)}
              </ExternalLink>
              {activeTab === INCOMPLETE_MY_CONSULTS_VALUE &&
                !healthieNoteId &&
                startTime > cutoffDate && <AlertTriangle />}
            </div>
          </>
        );
      },
    },
    {
      accessorKey: "consult",
      header: () => (
        <ScreenReaderOnlyText>{noteModalSrLabel}</ScreenReaderOnlyText>
      ),
      cell: (info: any) => {
        const cuser = React.useContext(CurrentUserContext);
        const consult = info.getValue();
        const [isOpen, setIsOpen] = useState(false);
        const currentPageUrl = window.location.href;
        const url = new URL(currentPageUrl);
        const domain = url.origin;
        //these are the clinician ids that have access to the google form
        const hasAccessToGoogleFormClinicianIds = [309, 503, 526];
        const hasAccessToGoogleForm =
          cuser?.clinician &&
          hasAccessToGoogleFormClinicianIds.includes(
            Math.abs(cuser?.clinician?.id),
          );
        return (
          <>
            <Flex>
              <div style={{ display: "flex", flexDirection: "row" }}>
                {activeTab === PAST_MY_CONSULTS_VALUE ||
                activeTab === SEARCH_ALL ? (
                  <Root
                    onOpenChange={(open: boolean) => {
                      if (!open) {
                        if (activeTab === PAST_MY_CONSULTS_VALUE) {
                          queryClient.invalidateQueries([
                            myConsultsQueryKey,
                            MyPastConsultsSearchResultsQueryKey,
                          ]);
                          refetchMyConsults();
                        } else {
                          queryClient.invalidateQueries([
                            matchesByClinicianIdQueryKey,
                          ]);
                          refetchMatchesByClinicianId();
                        }
                      }
                    }}
                  >
                    <Dialog.Portal>
                      <Overlay className="DialogOverlay" />
                      <Content
                        className="DialogContent"
                        style={{
                          padding: "10px",
                          minWidth: "900px",
                          height: "700px",
                          overflowY: "scroll",
                        }}
                      >
                        {consult.match?.client_status === "In Queue" ? (
                          <MatchQueueItem
                            matchId={consult.match?.id}
                            matchData={matchData}
                          />
                        ) : (
                          <MatchInfo2 matchId={consult.match?.id} />
                        )}

                        <Dialog.DialogClose asChild>
                          <ModalIconCloseButton>
                            {" "}
                            <XIcon></XIcon>
                          </ModalIconCloseButton>
                        </Dialog.DialogClose>
                      </Content>
                    </Dialog.Portal>
                  </Root>
                ) : null}
                {hasAccessToGoogleForm && (
                  <Popover open={isOpen} onOpenChange={setIsOpen}>
                    <PopoverTrigger asChild>
                      <UnstyledButton style={{ display: "flex" }}>
                        <ThreeDotsIcon />
                      </UnstyledButton>
                    </PopoverTrigger>
                    <PopoverContent sideOffset={12}>
                      <a
                        style={{
                          padding: "8px",
                          borderRadius: "4px",
                          boxShadow:
                            "0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 9px 28px 8px rgba(0, 0, 0, 0.05)",
                        }}
                        target="_blank"
                        href={`https://docs.google.com/forms/d/e/1FAIpQLSf5JXMQuCr-f7oUmGNyBIYn2IKIfi8ryMAvdDVLzWBFlT_brA/viewform?entry.2107733448=${domain}/my-clients/${consult?.client.id}`}
                        rel="noreferrer"
                      >
                        Match Client Biweekly
                      </a>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </Flex>
          </>
        );
      },
    },
  ];

  if (hasConsultPermissions) {
    columns.splice(3, 0, {
      accessorKey: "client",
      id: "consultTool",
      header: () => <ScreenReaderOnlyText>{toolSrLabel}</ScreenReaderOnlyText>,
      cell: (info: any) => {
        const client = info.getValue();
        const clientId =
          activeTab === PAST_MY_CONSULTS_VALUE || activeTab === SEARCH_ALL
            ? client.clientId
            : client?.id;
        const consultId = info.row?.original?.consult?.consultId;
        const matchId =
          info.row?.original?.consult?.match?.id ??
          info.row?.original?.consult?.matchId;

        const {
          data: missingConsentsData,
          isLoading: isMissingConsentsDataLoading,
        } = useGetClientMissingConsents({
          clientId,
          enabled: true,
        });

        const {
          data: clientPhotoIdData,
          isLoading: isClientPhotoIdDataLoading,
        } = useGetClientDocumentByType({
          clientId,
          documentType: "photo-id",
          enabled: true,
        });

        const bookingData =
          info.row?.original?.consult?.client?.ehr_booking?.[0];
        const consultServiceType = info.row?.original?.consult?.serviceType;
        const isPsychiatryAssessment =
          consultServiceType === "psychiatry_assessment";

        const hasConsented = missingConsentsData?.length === 0;

        const consultToolStartDate = DateTime.fromISO("2024-09-30");
        const createdAtDate = DateTime.fromISO(bookingData?.created_at);
        const needsInterpretation =
          info.row?.original?.client?.needsInterpretation;
        const cuser = React.useContext(CurrentUserContext);
        const isNeedsInterpretationFeatureEnabled =
          cuser?.permissions?.includes("view_interpreter_services_enabled");
        // Rows sourced from a consult tool rather than a match have no match to deep
        // link into, so they fall back to the plain consult tool route.
        const consultToolLink =
          (activeTab === PAST_MY_CONSULTS_VALUE || activeTab === SEARCH_ALL) &&
          matchId
            ? `${getRoofDogBaseUrl()}/my-clients/${clientId}/consult/${consultId}/match/${matchId}`
            : `${getRoofDogBaseUrl()}/my-clients/${clientId}/consult/${consultId}`;

        const getTagTitle = React.useMemo(() => {
          if (
            activeTab === PAST_MY_CONSULTS_VALUE ||
            isClientPhotoIdDataLoading ||
            isMissingConsentsDataLoading
          )
            return undefined;

          if (
            bookingData &&
            !hasConsented &&
            createdAtDate > consultToolStartDate
          ) {
            if (
              isPsychiatryAssessment &&
              bookingData?.appointment_type === "psychiatry_assessment" &&
              !clientPhotoIdData &&
              !isClientPhotoIdDataLoading
            ) {
              return "Missing info";
            }
            return "Consent needed";
          }

          if (
            isPsychiatryAssessment &&
            bookingData?.appointment_type === "psychiatry_assessment" &&
            !clientPhotoIdData
          ) {
            return "Missing ID";
          }

          return undefined;
        }, [
          activeTab,
          hasConsented,
          createdAtDate,
          bookingData?.appointment_type,
          clientPhotoIdData,
          isClientPhotoIdDataLoading,
          isPsychiatryAssessment,
        ]);

        return (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <a
              href={consultToolLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline" }}
            >
              {getConsultToolLabel(
                info.row?.original?.consult?.serviceType ?? "Consult",
              )}
            </a>

            {Boolean(getTagTitle) && (
              <Tag color={"red"} align={"center"}>
                <AlertTriangle height={16} width={16} />
                <span>{getTagTitle}</span>
              </Tag>
            )}

            {needsInterpretation && isNeedsInterpretationFeatureEnabled && (
              <Tag color={"violet"}>Needs Interpretation</Tag>
            )}
          </div>
        );
      },
    });
  }
  return columns;
};

interface FilterOption {
  label: string;
  value: boolean;
}

interface ConsultsTableContainerProps {
  data: FormattedRecentConsult[] | FormattedPastRow[];
  activeTab: string;
  searchFilter?: string;
  isLoading: boolean;
  isError?: boolean;
  onSearch: (searchTerm: string) => void;
  onSelectClientId?: (searchTerm: string) => void;
  refetchMyConsults: () => void;
  selectedClientId?: string;
  refetchMatchesByClinicianId: () => void;
  onFilterClick: (index: number, value: boolean, filterType: string) => void;
  onClearFilters: (key: keyof typeof INIT_FILTERS) => void;
  filterDropdowns: {
    client_status: FilterOption[];
    month: FilterOption[];
    year: FilterOption[];
  };
}

// change all `false` back to `isLoading`
export const ConsultsTableContainer = ({
  data,
  activeTab,
  isLoading,
  isError = false,
  onSearch,
  onSelectClientId,
  selectedClientId,
  searchFilter,
  onFilterClick,
  onClearFilters,
  filterDropdowns,
  refetchMyConsults,
  refetchMatchesByClinicianId,
}: ConsultsTableContainerProps) => {
  const isPathwaysMilestone3 = useIsPathwaysMilestone3();
  const [isDropdownOpen, setIsDropdownOpen] = useState({
    client_status: false,
    month: false,
    year: false,
  });

  const table = useReactTable({
    //@ts-ignore
    data,
    columns: getColumns({
      activeTab,
      matchData: data,
      refetchMatchesByClinicianId,
      refetchMyConsults,
      isPathwaysMilestone3,
    }),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    debugRows: false,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
  });

  const myMatches = useContext(ConsultClinicianClientsContext);
  const allClientsMap =
    myMatches?.length > 0
      ? getClientsMapFromPastConsultsData(myMatches)
      : new Map();
  let searchableClients = Array.from(allClientsMap.values());

  if (searchFilter) {
    searchableClients = searchableClients.filter((client) => {
      const fullName = client.fullName.toLowerCase();
      const payerAcronym = client.payerAcronym?.toLowerCase();
      const payerDisplayName = client.payerDisplayName?.toLowerCase();
      const searchTerm = searchFilter.toLowerCase();
      return (
        fullName.includes(searchTerm) ||
        payerAcronym?.includes(searchTerm) ||
        payerDisplayName?.includes(searchTerm)
      );
    });
  }

  return (
    <section style={{ width: "100%" }}>
      <Spin size="large" spinning={isLoading} aria-busy={isLoading}>
        <Container>
          {data && (
            <>
              {activeTab === PAST_MY_CONSULTS_VALUE ? (
                <PastConsultsTableContainer
                  onSearch={onSearch}
                  onSelectClientId={onSelectClientId}
                  clients={searchableClients}
                  isDropdownOpen={isDropdownOpen}
                  setIsDropdownOpen={setIsDropdownOpen}
                  onFilterClick={onFilterClick}
                  onClearFilters={onClearFilters}
                  filterDropdowns={filterDropdowns}
                />
              ) : null}
              {data?.length ? (
                <Flex flexDirection={"column"} gap="16">
                  <UnsortableDashboardTable
                    aria-labelledby="myConsultsV2Table"
                    fontWeight="regularFontWeight"
                  >
                    <thead>
                      {table.getHeaderGroups().map((headerGroup, index) => (
                        <tr key={index}>
                          {headerGroup.headers.map((header) => (
                            <th
                              style={{
                                ...(header.column.id === "fullName" && {
                                  width: "18%",
                                }),
                                ...(header.column.id === "consult" && {
                                  width: "10%",
                                }),
                                ...(header.column.id === "consultTool" && {
                                  width: "15%",
                                }),
                                ...(header.column.id === "time" && {
                                  width: "7%",
                                }),
                                ...(header.column.id === "startTime" && {
                                  width: "10%",
                                }),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row, index) => (
                        <tr key={index}>
                          {row.getVisibleCells().map((cell, index2) => (
                            <td key={index2}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </UnsortableDashboardTable>
                  <Flex
                    gap="16"
                    alignItems={"center"}
                    justifyContent={"flex-end"}
                  >
                    <Flex gap="8" alignItems={"center"}>
                      <Button
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                        variant={"tertiary"}
                        size={"small"}
                        gap="4"
                        aria-label="Go to first page"
                      >
                        {"<<"}
                      </Button>
                      <Button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        variant={"tertiary"}
                        size={"small"}
                        gap="4"
                        aria-label="Go to previous page"
                      >
                        {"<"}
                      </Button>
                      <Button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        variant={"tertiary"}
                        size={"small"}
                        gap="4"
                        aria-label="Go to next page"
                      >
                        {">"}
                      </Button>
                      <Button
                        onClick={() =>
                          table.setPageIndex(table.getPageCount() - 1)
                        }
                        disabled={!table.getCanNextPage()}
                        variant={"tertiary"}
                        size={"small"}
                        gap="4"
                        aria-label="Go to last page"
                      >
                        {">>"}
                      </Button>
                    </Flex>

                    <Flex alignItems={"center"}>
                      <span>
                        Page{" "}
                        <strong>
                          {table.getState().pagination.pageIndex + 1} of{" "}
                          {table.getPageCount()}
                        </strong>
                      </span>
                    </Flex>
                    <Flex gap="4" alignItems={"center"}>
                      Go to page:
                      <PaginationInput
                        type="number"
                        defaultValue={table.getState().pagination.pageIndex + 1}
                        onChange={(e) => {
                          const page = e.target.value
                            ? Number(e.target.value) - 1
                            : 0;
                          table.setPageIndex(page);
                        }}
                        min="1"
                        max={table.getPageCount()}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              ) : (
                <>
                  {isLoading ? null : (
                    <HintTextDiv
                      card={"white"}
                      style={{ backgroundColor: "transparent" }}
                    >
                      {isError ? (
                        <span>
                          {isPathwaysMilestone3
                            ? "There was an error fetching assessment data"
                            : "There was an error fetching consult data"}
                        </span>
                      ) : activeTab === INCOMPLETE_MY_CONSULTS_VALUE ? (
                        <span>
                          {isPathwaysMilestone3
                            ? "All assessment notes have been completed"
                            : "All consult notes have been completed"}
                        </span>
                      ) : (
                        <NoDataMessage />
                      )}
                    </HintTextDiv>
                  )}
                </>
              )}
            </>
          )}
        </Container>
      </Spin>
    </section>
  );
};
